import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import Card from './ui/Card';
import ValueSlider from './ui/ValueSlider';
import { ButtonRow, GoldButton, OutlineButton } from './ui/Buttons';

interface BackgroundImageAdjustmentModalProps {
	open: boolean;
	backgroundImage: string;
	initialGrayscale?: number;
	initialOpacity?: number;
	onConfirm: (grayscale: number, opacity: number) => void;
	onSkip: () => void;
	step?: number;
	totalSteps?: number;
}

/** "Adjust background image" wizard card (style guide R14 step 3). */
const BackgroundImageAdjustmentModal: FC<BackgroundImageAdjustmentModalProps> = ({ open, backgroundImage, initialGrayscale = 0, initialOpacity = 100, onConfirm, onSkip, step, totalSteps }) => {
	const [grayscale, setGrayscale] = useState(initialGrayscale);
	const [opacity, setOpacity] = useState(initialOpacity);

	useEffect(() => {
		if (open) {
			setGrayscale(initialGrayscale);
			setOpacity(initialOpacity);
		}
	}, [open, initialGrayscale, initialOpacity]);

	return (
		<Card open={open} title="Adjust background image" step={step} totalSteps={totalSteps} description="Customise the appearance of your background image so it is easy to trace over.">
			<PreviewBox>
				<PreviewImage src={backgroundImage} alt="Background preview" $grayscale={grayscale} $opacity={opacity / 100} />
			</PreviewBox>

			<ValueSlider label="Greyscale" value={grayscale} min={0} max={100} onChange={setGrayscale} variant="gold" inlineValue={v => `Greyscale: ${v}%`} />
			<ValueSlider label="Opacity" value={opacity} min={0} max={100} onChange={setOpacity} variant="gold" inlineValue={v => `Opacity: ${v}%`} />

			<ButtonRow>
				<OutlineButton type="button" onClick={onSkip}>
					Skip
				</OutlineButton>
				<GoldButton type="button" onClick={() => onConfirm(grayscale, opacity / 100)}>
					Apply
				</GoldButton>
			</ButtonRow>
		</Card>
	);
};

export default BackgroundImageAdjustmentModal;

const PreviewBox = styled.div`
	display: flex;
	justify-content: center;
	margin: 22px 0 8px;
`;

const PreviewImage = styled.img<{ $grayscale: number; $opacity: number }>`
	width: 140px;
	height: 140px;
	object-fit: contain;
	border-radius: 10px;
	background-color: #000;
	filter: grayscale(${props => props.$grayscale}%);
	opacity: ${props => props.$opacity};
	transition: filter 0.2s ease, opacity 0.2s ease;
`;
